import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import ContactRequestModal from "../components/ContactRequestModal";
import {
  ArrowLeft, Globe, Phone, Mail, MessageCircle,
  Linkedin, CheckCircle2, Star, Sparkles, Briefcase, Star as StarIcon,
  Flame, MapPin, Calendar, Send, X, UserPlus
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = { cherche: "Je cherche", propose: "Je propose", opportunite: "Opportunité" };

function Avatar({ profile, size = 80 }) {
  const initials = (profile?.nom_prenom || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  if (profile?.photo) {
    return <img src={`${API}${profile.photo}`} alt={profile.nom_prenom} className="rounded-full object-cover flex-shrink-0 border-2 border-gold/20" style={{ width: size, height: size }} />;
  }
  return (
    <div className="cbm-avatar flex-shrink-0 text-gold font-outfit font-bold border-2 border-gold/20"
      style={{ width: size, height: size, fontSize: size * 0.35, background: "linear-gradient(135deg, #26406E, #1A2E5C)" }}>
      {initials}
    </div>
  );
}

function Stars({ value, size = 14, interactive = false, onChange }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(n => {
        const filled = value >= n;
        const half = !filled && value >= n - 0.5;
        return (
          <button
            key={n}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && onChange?.(n)}
            className={`${interactive ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}`}
            data-testid={interactive ? `rating-star-${n}` : undefined}
          >
            <Star
              size={size}
              className={filled ? "fill-gold text-gold" : half ? "fill-gold/50 text-gold" : "text-gold/30"}
            />
          </button>
        );
      })}
    </div>
  );
}

function ReviewForm({ profileId, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!rating || rating < 1) {
      setError("Veuillez sélectionner une note entre 1 et 5");
      return;
    }
    if (!comment.trim()) {
      setError("Veuillez rédiger un commentaire");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/reviews`,
        { profile_id: profileId, rating, comment: comment.trim() },
        { withCredentials: true }
      );
      setSuccess(true);
      onSubmitted?.();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="cbm-card p-5 text-center" data-testid="review-submitted">
        <CheckCircle2 size={28} className="text-green-400 mx-auto mb-2" />
        <p className="text-white font-medium text-sm">Avis envoyé !</p>
        <p className="text-cbm-muted text-xs mt-1">Il sera publié après validation par le membre concerné.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="cbm-card p-5" data-testid="review-form">
      <p className="cbm-label mb-3">Laisser un avis</p>
      <div className="flex items-center gap-3 mb-3">
        <span className="text-cbm-secondary text-sm">Votre note :</span>
        <Stars value={rating} size={22} interactive onChange={setRating} />
        {rating > 0 && <span className="text-gold text-sm font-medium">{rating}/5</span>}
      </div>
      <textarea
        data-testid="review-comment-input"
        className="cbm-input mb-3"
        rows={3}
        placeholder="Partagez votre expérience avec ce membre..."
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        maxLength={800}
      />
      {error && <p className="text-red-400 text-xs mb-2" data-testid="review-error">{error}</p>}
      <div className="flex items-center justify-between">
        <p className="text-cbm-muted text-xs">{comment.length}/800 • Sera validé par le membre</p>
        <button
          data-testid="submit-review-button"
          type="submit"
          disabled={submitting}
          className="cbm-btn-primary text-sm"
        >
          {submitting ? "Envoi..." : <><Send size={13} /> Envoyer</>}
        </button>
      </div>
    </form>
  );
}

function ReviewItem({ review }) {
  const author = review.author || {};
  const initials = (author.nom_prenom || "M").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="cbm-card p-4" data-testid={`review-${review._id}`}>
      <div className="flex items-start gap-3">
        {author.photo ? (
          <img src={`${API}${author.photo}`} alt={author.nom_prenom} className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-gold/20" />
        ) : (
          <div className="cbm-avatar w-10 h-10 text-sm flex-shrink-0">{initials}</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <p className="text-white text-sm font-medium">{author.nom_prenom || "Membre"}</p>
              {author.entreprise && <p className="text-cbm-muted text-xs">{author.entreprise}</p>}
            </div>
            <div className="flex items-center gap-2">
              <Stars value={review.rating} size={13} />
              <span className="text-cbm-muted text-xs">{new Date(review.created_at).toLocaleDateString("fr-FR")}</span>
            </div>
          </div>
          <p className="text-cbm-secondary text-sm mt-2 whitespace-pre-line">{review.comment}</p>
        </div>
      </div>
    </div>
  );
}

export default function ProfileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [opps, setOpps] = useState([]);
  const [reviewsData, setReviewsData] = useState({ items: [], count: 0, average: 0, my_review: null, can_review: false });
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactStatus, setContactStatus] = useState(null);

  const loadContactStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/contact-requests/check/${id}`, { withCredentials: true });
      setContactStatus(data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/api/profiles/${id}/reviews`, { withCredentials: true });
      setReviewsData(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, oRes, rRes] = await Promise.all([
          axios.get(`${API}/api/profiles/${id}`, { withCredentials: true }),
          axios.get(`${API}/api/opportunities/profile/${id}`, { withCredentials: true }),
          axios.get(`${API}/api/profiles/${id}/reviews`, { withCredentials: true }),
        ]);
        setProfile(pRes.data);
        setOpps(oRes.data);
        setReviewsData(rRes.data);
        
        // Load contact status
        await loadContactStatus();
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const trackAndExecute = (action) => {
    axios.post(`${API}/api/profiles/${profile._id}/contact-click`, {}, { withCredentials: true }).catch(() => {});
    action();
  };

  if (loading) return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="cbm-skeleton h-8 w-32 mb-6 rounded-lg" />
      <div className="cbm-card p-6 flex gap-5">
        <div className="cbm-skeleton rounded-full" style={{ width: 80, height: 80 }} />
        <div className="flex-1 space-y-3">
          <div className="cbm-skeleton h-6 w-48" />
          <div className="cbm-skeleton h-4 w-36" />
          <div className="cbm-skeleton h-4 w-24" />
        </div>
      </div>
    </div>
  );

  if (!profile) return (
    <div className="p-6 text-center">
      <p className="text-cbm-muted">Profil introuvable</p>
      <button className="cbm-btn-ghost mt-3" onClick={() => navigate("/annuaire")}>Retour à l'annuaire</button>
    </div>
  );

  const openOpps = opps.filter(o => o.statut === "ouvert");
  const closedOpps = opps.filter(o => o.statut === "cloture");
  const isOwnProfile = profile.user_id === (user?._id || user?.id);
  const myReview = reviewsData.my_review;

  return (
    <div className="p-6 max-w-4xl mx-auto animate-fade-in">
      {/* Back */}
      <button className="cbm-btn-ghost text-sm mb-5" onClick={() => navigate(-1)} data-testid="back-button">
        <ArrowLeft size={16} /> Retour
      </button>

      {/* Profile card */}
      <div className="cbm-card p-6 mb-4 animate-fade-in-up">
        <div className="flex flex-col sm:flex-row gap-5">
          <Avatar profile={profile} size={84} />
          <div className="flex-1">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-outfit font-bold text-white text-xl">{profile.nom_prenom}</h1>
                  {profile.is_verified && <CheckCircle2 size={18} className="text-green-400" />}
                </div>
                {profile.fonction && (
                  <p className="mt-0.5 text-sm font-semibold" style={{ color: "#C9A227" }} data-testid="profile-fonction">
                    {profile.fonction}
                  </p>
                )}
                {profile.entreprise && <p className="text-gold font-medium mt-0.5">{profile.entreprise}</p>}
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  {profile.secteur && (
                    <div className="flex items-center gap-1 text-cbm-muted text-sm">
                      <Briefcase size={13} /> {profile.secteur}
                    </div>
                  )}
                  {profile.ville && (
                    <div className="flex items-center gap-1 text-cbm-muted text-sm">
                      <MapPin size={13} /> {profile.ville}{profile.pays ? `, ${profile.pays}` : ""}
                    </div>
                  )}
                  {reviewsData.count > 0 && (
                    <div className="flex items-center gap-1.5 text-sm" data-testid="profile-avg-rating">
                      <Stars value={reviewsData.average} size={13} />
                      <span className="text-white font-medium">{reviewsData.average}</span>
                      <span className="text-cbm-muted">({reviewsData.count} avis)</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Badges */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {profile.is_ambassador && <span className="cbm-badge cbm-badge-ambassador" data-testid="badge-ambassador"><Sparkles size={9} className="mr-0.5" />Ambassadeur</span>}
                {profile.is_top_connector && <span className="cbm-badge cbm-badge-top-connector" data-testid="badge-top-connector"><Flame size={9} className="mr-0.5" />Top connecteur</span>}
                {profile.is_featured && <span className="cbm-badge cbm-badge-featured"><Star size={9} className="mr-0.5" />À la une</span>}
                {profile.is_recommended && <span className="cbm-badge cbm-badge-recommended"><Sparkles size={9} className="mr-0.5" />Recommandé</span>}
                {profile.is_new && <span className="cbm-badge cbm-badge-new">Nouveau</span>}
              </div>
            </div>

            {/* Contact actions */}
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              {/* Show "Demander le contact" button if no accepted relationship */}
              {!profile.has_accepted_contact && contactStatus?.can_request && (
                <button
                  onClick={() => setShowContactModal(true)}
                  className="cbm-btn-primary text-sm py-2 flex items-center gap-1.5"
                  data-testid="request-contact-button"
                >
                  <UserPlus size={15} /> Demander le contact
                </button>
              )}

              {/* Show status message for pending/refused */}
              {contactStatus?.status === "pending" && (
                <div className="bg-navy-darker rounded-lg p-3 text-cbm-secondary text-xs">
                  ⏳ {contactStatus.direction === "sent" ? "Demande envoyée, en attente de réponse" : "Demande de contact reçue"}
                </div>
              )}
              {contactStatus?.status === "refused_recent" && (
                <div className="bg-navy-darker rounded-lg p-3 text-cbm-secondary text-xs">
                  ⏰ Vous pourrez renvoyer une demande dans {contactStatus.retry_in_days} jour{contactStatus.retry_in_days > 1 ? "s" : ""}
                </div>
              )}

              {/* Show all contact buttons if relationship is accepted */}
              {profile.has_accepted_contact && (
                <>
                  {/* WhatsApp */}
                  {profile.whatsapp && (
                    <a
                      data-testid="whatsapp-button"
                      className="cbm-btn-primary text-sm py-2 inline-flex items-center gap-1.5"
                      href={`https://wa.me/${String(profile.whatsapp || "").replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        axios.post(`${API}/api/profiles/${profile._id}/contact-click`, {}, { withCredentials: true }).catch(() => {});
                      }}
                    >
                      <MessageCircle size={15} /> WhatsApp
                    </a>
                  )}
                  
                  {/* Appeler */}
                  {profile.phone_visible && profile.phone && (
                    <button
                      data-testid="phone-button"
                      className="cbm-btn-secondary text-sm py-2"
                      onClick={() => trackAndExecute(() => {
                        window.location.href = `tel:${profile.phone}`;
                      })}
                    >
                      <Phone size={15} /> Appeler
                    </button>
                  )}
                  
                  {/* Email */}
                  {profile.email_visible && profile.email && (
                    <button
                      data-testid="email-button"
                      className="cbm-btn-secondary text-sm py-2"
                      onClick={() => trackAndExecute(() => {
                        window.location.href = `mailto:${profile.email}`;
                      })}
                    >
                      <Mail size={15} /> Email
                    </button>
                  )}
                  
                  {/* Prendre rendez-vous */}
                  {profile.calendly_url && (
                    <a
                      href={profile.calendly_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cbm-btn-primary text-sm py-2"
                      data-testid="booking-button"
                      style={{ background: "rgba(201, 162, 39, 0.18)", color: "#C9A227", border: "1px solid #C9A227" }}
                      onClick={() => axios.post(`${API}/api/profiles/${profile._id}/contact-click`, {}, { withCredentials: true }).catch(() => {})}
                    >
                      <Calendar size={15} /> Prendre rendez-vous
                    </a>
                  )}
                  
                  {/* Plaquette commerciale - visible uniquement si contact accepté */}
                  {profile.brochure_url && profile.has_accepted_contact && (
                    <a
                      href={profile.brochure_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cbm-btn-secondary text-sm py-2"
                      data-testid="brochure-button"
                    >
                      <Briefcase size={15} /> Voir la plaquette
                    </a>
                  )}
                  
                  {/* LinkedIn */}
                  {profile.linkedin_url && (
                    <a
                      href={profile.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cbm-btn-secondary text-sm py-2"
                      data-testid="linkedin-button"
                      onClick={() => axios.post(`${API}/api/profiles/${profile._id}/contact-click`, {}, { withCredentials: true }).catch(() => {})}
                    >
                      <Linkedin size={15} /> LinkedIn
                    </a>
                  )}
                  
                  {/* Site web */}
                  {profile.website && (
                    <a
                      href={profile.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cbm-btn-ghost text-sm"
                      data-testid="website-button"
                      onClick={() => axios.post(`${API}/api/profiles/${profile._id}/contact-click`, {}, { withCredentials: true }).catch(() => {})}
                    >
                      <Globe size={14} /> Site web
                    </a>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Offre */}
        {profile.offre && (
          <div className="cbm-card p-5 animate-fade-in-up-delay-1">
            <p className="cbm-label mb-2">Ce que je propose</p>
            <p className="text-cbm-secondary text-sm leading-relaxed">{profile.offre}</p>
          </div>
        )}
        {/* Je peux aider */}
        {profile.je_peux_aider && (
          <div className="cbm-card p-5 animate-fade-in-up-delay-2">
            <p className="cbm-label mb-2">Je peux aider avec</p>
            <p className="text-cbm-secondary text-sm leading-relaxed">{profile.je_peux_aider}</p>
          </div>
        )}
      </div>

      {/* References */}
      {profile.references && (
        <div className="cbm-card p-5 mb-4 animate-fade-in-up-delay-2" data-testid="profile-references">
          <p className="cbm-label mb-2">Références</p>
          <p className="text-cbm-secondary text-sm leading-relaxed whitespace-pre-line">{profile.references}</p>
        </div>
      )}

      {/* Appartenances professionnelles */}
      {((profile.federations && profile.federations.length > 0) || (profile.business_clubs && profile.business_clubs.length > 0)) && (
        <div className="cbm-card p-5 mb-4 animate-fade-in-up-delay-3" data-testid="profile-memberships">
          <p className="cbm-label mb-3">Appartenances professionnelles</p>
          <div className="space-y-2.5">
            {profile.federations && profile.federations.length > 0 && (
              <div>
                <p className="text-cbm-muted text-xs mb-1">Fédération / organisation</p>
                <p className="text-cbm-secondary text-sm">
                  {profile.federations.map((fed, idx) => (
                    <span key={idx}>
                      {fed === "Autre" && profile.other_federation ? profile.other_federation : fed}
                      {idx < profile.federations.length - 1 && ", "}
                    </span>
                  ))}
                </p>
              </div>
            )}
            {profile.business_clubs && profile.business_clubs.length > 0 && (
              <div>
                <p className="text-cbm-muted text-xs mb-1">Autre club / réseau business</p>
                <p className="text-cbm-secondary text-sm">
                  {profile.business_clubs.map((club, idx) => (
                    <span key={idx}>
                      {club === "Autre" && profile.other_business_club ? profile.other_business_club : club}
                      {idx < profile.business_clubs.length - 1 && ", "}
                    </span>
                  ))}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Opportunities */}
      {opps.length > 0 && (
        <div className="animate-fade-in-up-delay-3 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-outfit font-semibold text-white text-base">Annonces</h2>
            <div className="flex gap-2">
              <span className="cbm-badge cbm-badge-open">{openOpps.length} ouvertes</span>
              {closedOpps.length > 0 && <span className="cbm-badge cbm-badge-closed">{closedOpps.length} clôturées</span>}
            </div>
          </div>
          <div className="space-y-3">
            {opps.map(opp => (
              <div key={opp._id} className="cbm-card p-4 flex items-start gap-3" data-testid={`profile-opp-${opp._id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`cbm-badge ${opp.type === "cherche" ? "cbm-badge-type-cherche" : opp.type === "propose" ? "cbm-badge-type-propose" : "cbm-badge-type-opportunite"}`}>
                      {TYPE_LABELS[opp.type] || opp.type}
                    </span>
                    <span className={`cbm-badge ${opp.statut === "ouvert" ? "cbm-badge-open" : "cbm-badge-closed"}`}>
                      {opp.statut === "ouvert" ? "Ouvert" : "Clôturé"}
                    </span>
                    {opp.is_featured && <span className="cbm-badge cbm-badge-featured"><StarIcon size={9} className="mr-0.5" />À la une</span>}
                    {opp.is_priority && <span className="cbm-badge cbm-badge-priority"><Flame size={9} className="mr-0.5" />Prioritaire</span>}
                  </div>
                  <h4 className="text-white text-sm font-medium">{opp.titre}</h4>
                  {opp.description && <p className="text-cbm-muted text-xs mt-1 line-clamp-1">{opp.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reviews section */}
      <div className="animate-fade-in-up-delay-3" data-testid="reviews-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-outfit font-semibold text-white text-base">
            Avis {reviewsData.count > 0 && <span className="text-cbm-muted text-sm font-normal">({reviewsData.count})</span>}
          </h2>
          {reviewsData.count > 0 && (
            <div className="flex items-center gap-2">
              <Stars value={reviewsData.average} size={16} />
              <span className="text-white font-medium text-sm">{reviewsData.average}/5</span>
            </div>
          )}
        </div>

        {/* My existing review status (if any) */}
        {myReview && (
          <div className="cbm-card p-4 mb-3 border-l-2 border-gold" data-testid="my-existing-review">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="flex items-center gap-2">
                <span className="cbm-label">Votre avis</span>
                <Stars value={myReview.rating} size={12} />
                <span className="text-cbm-muted text-xs">{new Date(myReview.created_at).toLocaleDateString("fr-FR")}</span>
              </div>
              <span className={`cbm-badge ${myReview.status === "approved" ? "cbm-badge-open" : myReview.status === "rejected" ? "cbm-badge-closed" : "cbm-badge-new"}`}>
                {myReview.status === "approved" ? "Publié" : myReview.status === "rejected" ? "Refusé" : "En attente de modération"}
              </span>
            </div>
            <p className="text-cbm-secondary text-sm whitespace-pre-line">{myReview.comment}</p>
          </div>
        )}

        {/* Submission form — shown only when the current user has an accepted contact */}
        {!isOwnProfile && !myReview && reviewsData.can_review && (
          <div className="mb-3">
            <ReviewForm profileId={id} onSubmitted={loadReviews} />
          </div>
        )}

        {/* Not connected → explain why the form is hidden */}
        {!isOwnProfile && !myReview && !reviewsData.can_review && (
          <div
            className="cbm-card p-4 mb-3 text-center"
            data-testid="reviews-gate-message"
            style={{ background: "rgba(155,169,202,0.05)", border: "1px dashed rgba(155,169,202,0.2)" }}
          >
            <p className="text-cbm-secondary text-sm">
              Vous devez être connecté avec ce membre pour laisser un avis.
            </p>
          </div>
        )}

        {/* Approved reviews list */}
        {reviewsData.items.length > 0 ? (
          <div className="space-y-3">
            {reviewsData.items.map(r => <ReviewItem key={r._id} review={r} />)}
          </div>
        ) : (
          !myReview && !isOwnProfile && (
            <p className="text-cbm-muted text-sm text-center py-2">Aucun avis pour le moment. Soyez le premier !</p>
          )
        )}

        {isOwnProfile && reviewsData.items.length === 0 && (
          <p className="text-cbm-muted text-sm text-center py-4">Les avis reçus apparaîtront ici après validation.</p>
        )}
      </div>

      {/* Contact Request Modal */}
      {showContactModal && (
        <ContactRequestModal
          profile={profile}
          onClose={() => setShowContactModal(false)}
          onSuccess={() => {
            loadContactStatus();
            alert("Demande envoyée avec succès !");
          }}
        />
      )}
    </div>
  );
}
